/**
 * Ticket 138.2 — thin wrapper over Meta's JS SDK for the Embedded Signup popup.
 *
 * Why this is its own module and not inline in the page: the page must stay
 * catalogue-driven and name no provider, and the SDK is a global side effect
 * (a script tag, a `window.FB`, a `message` listener) that a component test
 * cannot exercise. Isolating it here lets the page depend on a plain async
 * function and lets the tests substitute one.
 *
 * On the version: the flow runs Embedded Signup **v4**, and there is no
 * parameter here that says so. Meta selects the version from the products
 * chosen in the Facebook Login for Business configuration the `configId`
 * points at — `sessionInfoVersion` is not a parameter of this flow and adding
 * one would be inventing an API.
 *
 * What comes back is deliberately narrow: the short-lived authorization code
 * and the two ids the popup reports. No access token is ever minted in the
 * browser; the code is exchanged server-side, synchronously, because it lives
 * about thirty seconds.
 */

export interface EmbeddedSignupOutcome {
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

export interface EmbeddedSignupLaunchParams {
  appId: string;
  configId: string;
  graphVersion: string;
}

/**
 * The exact origins Meta's Embedded Signup popup posts from.
 *
 * This is an exact-match allowlist and not a suffix test on purpose. The first
 * version of this check was `origin.endsWith('facebook.com')`, which also
 * accepts `https://evilfacebook.com` and `https://notfacebook.com` — a domain
 * anyone can register. Suffix matching on a host is a recurring way to write a
 * check that reads as an origin check and is not one.
 *
 * Full origins, so the scheme is checked too: `http://www.facebook.com` is a
 * different origin from the https one and must not be believed either.
 */
const TRUSTED_POPUP_ORIGINS: ReadonlySet<string> = new Set([
  'https://www.facebook.com',
  'https://web.facebook.com',
  'https://business.facebook.com',
  'https://m.facebook.com',
]);

/** Raised when the admin closes the popup or declines. Not an error to log. */
export class EmbeddedSignupCancelled extends Error {
  constructor(message = 'embedded signup was cancelled') {
    super(message);
    this.name = 'EmbeddedSignupCancelled';
  }
}

interface FacebookLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}

interface FacebookSdk {
  init(params: { appId: string; version: string; xfbml?: boolean; autoLogAppEvents?: boolean }): void;
  login(
    callback: (response: FacebookLoginResponse) => void,
    options: {
      config_id: string;
      response_type: string;
      override_default_response_type: boolean;
      extras: { setup: Record<string, unknown>; featureType?: string };
    },
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_SCRIPT_ID = 'facebook-jssdk';

/**
 * Loads the SDK once and initialises it for `appId`. Subsequent calls with the
 * same app resolve immediately.
 */
function loadSdk(params: EmbeddedSignupLaunchParams): Promise<FacebookSdk> {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    const existing = document.getElementById(SDK_SCRIPT_ID);
    const finish = () => {
      if (!window.FB) {
        reject(new Error('the Meta JS SDK loaded without exposing FB'));
        return;
      }
      window.FB.init({
        appId: params.appId,
        version: params.graphVersion,
        xfbml: false,
        autoLogAppEvents: true,
      });
      resolve(window.FB);
    };
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => reject(new Error('the Meta JS SDK failed to load')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.id = SDK_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://connect.facebook.net/en_US/sdk.js`;
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('the Meta JS SDK failed to load')), {
      once: true,
    });
    document.body.appendChild(script);
  });
}

/**
 * Opens the popup and resolves with the code plus the session ids.
 *
 * The two halves arrive by different routes — the ids over `postMessage`, the
 * code in the login callback — so both are collected before resolving.
 */
export async function launchEmbeddedSignup(
  params: EmbeddedSignupLaunchParams,
): Promise<EmbeddedSignupOutcome> {
  const fb = await loadSdk(params);

  return new Promise<EmbeddedSignupOutcome>((resolve, reject) => {
    let session: { wabaId: string; phoneNumberId: string } | null = null;

    const onMessage = (event: MessageEvent) => {
      // Only Meta's own popup origins are trusted; anything else on the page
      // can postMessage, and this payload decides which line gets onboarded.
      if (!TRUSTED_POPUP_ORIGINS.has(event.origin)) return;
      let payload: unknown;
      try {
        payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      const message = payload as
        | { type?: string; event?: string; data?: { waba_id?: string; phone_number_id?: string } }
        | undefined;
      if (message?.type !== 'WA_EMBEDDED_SIGNUP') return;
      if (message.event === 'FINISH' && message.data?.waba_id && message.data?.phone_number_id) {
        session = { wabaId: message.data.waba_id, phoneNumberId: message.data.phone_number_id };
      }
    };
    window.addEventListener('message', onMessage);
    const done = () => window.removeEventListener('message', onMessage);

    fb.login(
      (response) => {
        done();
        const code = response?.authResponse?.code;
        if (!code) {
          reject(new EmbeddedSignupCancelled());
          return;
        }
        if (!session) {
          reject(new Error('the popup completed without reporting a WhatsApp account'));
          return;
        }
        resolve({ code, wabaId: session.wabaId, phoneNumberId: session.phoneNumberId });
      },
      {
        config_id: params.configId,
        // A CODE, not a token: the browser must never hold a business token.
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {} },
      },
    );
  });
}
